import React, { useState } from 'react'
import { useIntl } from 'react-intl'
import { Icon, InputInline, SelectInline } from '@arnsbogroup/react-voicearchive'
import {
  FILE_NAMING_PRESETS,
  FILE_NAMING_OPTIONS,
  FILE_NAMING_MARKERS,
  getFileNamingPreset,
} from 'components/FileNaming'
import messages from './messages'

// Order-level file naming. Empty (null) inherits the company convention from the
// customer profile; anything else applies to this order only.
// See documentation/FILE_NAMING.md
const OrderFileNaming = ({ order, onChange, readOnly }) => {
  const { formatMessage } = useIntl()
  // "Custom" picked on a value that is still a preset: keep showing Custom while it is edited
  const [customPicked, setCustomPicked] = useState(false)

  const companyConvention = order.customer?.filenameConvention || ''
  const override = order.filenameConvention || null
  const effectiveConvention = override || companyConvention

  const companyOption = {
    value: 'company',
    label: formatMessage(messages.company, { preset: getFileNamingPreset(companyConvention).label }),
  }
  const options = [companyOption, ...FILE_NAMING_OPTIONS]
  const selected = !override
    ? companyOption
    : customPicked
      ? options.find(({ value }) => value === 'custom')
      : getFileNamingPreset(override)

  const setConvention = (convention) => {
    onChange({ ...order, filenameConvention: convention?.trim() || null })
  }

  const onPresetChange = ({ value }) => {
    setCustomPicked(value === 'custom')

    if (value === 'company') {
      setConvention(null)
    } else if (value === 'custom') {
      // start from what applies now, so it can be adjusted rather than retyped
      setConvention(effectiveConvention)
    } else {
      setConvention(FILE_NAMING_PRESETS[value])
    }
  }

  return (
    <div className="mt-2 flex items-center text-sm text-gray-500" title={formatMessage(messages.title)}>
      <Icon icon={['fas', 'file-signature']} className="mr-1.5 shrink-0 text-gray-400" aria-hidden="true" />

      <div className="shrink-0 mr-2">
        <SelectInline
          disabled={readOnly}
          value={selected}
          options={options}
          onChange={onPresetChange}
        />
      </div>

      <InputInline
        disabled={readOnly}
        type="text"
        placeholder={companyConvention}
        value={override || ''}
        onChange={({ target: { value } }) => {
          setCustomPicked(false)
          setConvention(value)
        }}
      />

      <span
        className="ml-1.5 cursor-help"
        title={[
          formatMessage(messages.markers),
          ...FILE_NAMING_MARKERS.map(([marker, description]) => `${marker}  ${description}`),
        ].join('\n')}
      >
        <Icon icon={['fas', 'info-circle']} className="text-gray-400" aria-hidden="true" />
      </span>
    </div>
  )
}

export default OrderFileNaming
